// Cross-platform screen-protection hook for VIP / Chat pages.
//
// Android:
//   @capacitor/privacy-screen sets FLAG_SECURE on the Activity → blocks
//   screenshots and screen recording natively, and also blurs the
//   app-switcher preview. Works perfectly with the existing v1.8+ native
//   shell which already includes the plugin Kotlin code.
//
// iOS:
//   Apple does NOT expose any API equivalent to FLAG_SECURE.
//   @capacitor/privacy-screen on iOS only blurs the app-switcher preview;
//   it does NOT block direct screenshots or screen recordings.
//
//   To truly black-out screenshots / screen recordings on iOS we use the
//   industry-standard "secure UITextField" trick implemented in our custom
//   Swift plugin IOSScreenProtectorPlugin.swift (registered in
//   AppDelegate.swift). Same technique used by Netflix, Revolut, banking
//   apps. We invoke it via Capacitor's `registerPlugin` bridge.
//
//   We ALSO keep @capacitor/privacy-screen enabled on iOS in parallel so
//   the app-switcher preview is blurred too (the two mechanisms cover
//   different attack vectors and don't conflict).
//
// Static imports (NOT dynamic) so the plugins are available the instant
// React mounts the protected page — no lazy chunk loading races (the
// Android race-condition bug we hit on 2026-06-04 was caused by a dynamic
// import losing to the activity foreground transition).
import { useEffect } from 'react';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { PrivacyScreen } from '@capacitor/privacy-screen';

// Bridge to the custom Swift plugin. On non-iOS platforms `registerPlugin`
// returns a no-op proxy whose method calls reject — we guard with
// try/catch so the failure is silent. The string identifier must match
// `jsName` in IOSScreenProtectorPlugin.swift.
const IOSScreenProtector = registerPlugin('IOSScreenProtector');

export const useScreenProtection = (skip = false) => {
  useEffect(() => {
    if (skip) return;
    if (!Capacitor.isNativePlatform()) return;

    const isIOS = Capacitor.getPlatform() === 'ios';
    let cancelled = false;
    let didEnablePrivacyScreen = false;
    let didEnableIOSProtector = false;

    // Always try the privacy-screen plugin (Android FLAG_SECURE, iOS
    // app-switcher blur). Guard against very old native shells without
    // the plugin (will simply no-op).
    const privacyAvailable =
      PrivacyScreen && typeof PrivacyScreen.enable === 'function';

    if (privacyAvailable) {
      PrivacyScreen.enable()
        .then((res) => {
          if (cancelled) {
            PrivacyScreen.disable().catch(() => { /* noop */ });
            return;
          }
          didEnablePrivacyScreen = !!(res && res.success !== false);
          // eslint-disable-next-line no-console
          console.log('[screen-protection] privacy-screen enable →', res);
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[screen-protection] privacy-screen enable failed:', err?.message || err);
        });
    }

    // On iOS, additionally invoke the custom secure-text-entry plugin to
    // truly black-out screenshots / screen recordings / AirPlay.
    if (isIOS) {
      IOSScreenProtector.enable()
        .then((res) => {
          if (cancelled) {
            IOSScreenProtector.disable().catch(() => { /* noop */ });
            return;
          }
          didEnableIOSProtector = !!(res && res.success !== false);
          // eslint-disable-next-line no-console
          console.log('[screen-protection] iOS secure-entry enable →', res);
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[screen-protection] iOS secure-entry enable failed:', err?.message || err);
        });
    }

    return () => {
      cancelled = true;
      if (didEnablePrivacyScreen) {
        PrivacyScreen.disable().catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[screen-protection] privacy-screen disable failed:', err?.message || err);
        });
      }
      if (didEnableIOSProtector) {
        IOSScreenProtector.disable().catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[screen-protection] iOS secure-entry disable failed:', err?.message || err);
        });
      }
    };
  }, [skip]);
};

export default useScreenProtection;
